/**
 * NotificationContainer Component
 * Manages positioning and rendering of notification stack
 */

import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { NotificationItem } from '../NotificationItem';
import { NotificationContainerProps, AnimationState } from '../../types';
import { useNotificationContext } from '../../context/NotificationContext';
import { 
  getContainerPositionStyles, 
  getViewportAwarePosition,
  getResponsiveWidth,
  getMobileStyles,
  getHeaderAwareOffset,
} from '../../utils/positioning';

/**
 * NotificationContainer Component
 */
export const NotificationContainer: React.FC<NotificationContainerProps> = ({
  notifications,
  config,
  onDismiss,
  onAnimationComplete,
}) => {
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [headerHeight, setHeaderHeight] = useState(0);
  const context = useNotificationContext();
  const isPanelOpen = context.notificationPanelOpen;
  const isDropdownOpen = context.headerDropdownOpen;
  const dropdownHeight = context.headerDropdownHeight;
  
  // Update viewport width on resize
  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Detect header height
  useEffect(() => {
    // Look for common header selectors
    const header = document.querySelector('header, .header, .app-header, .core-header');
    if (header) {
      const rect = header.getBoundingClientRect();
      setHeaderHeight(rect.height);
    }
  }, []);

  // Determine if mobile
  const isMobile = viewportWidth < 640;

  // Get viewport-aware position
  const adjustedPosition = useMemo(() => {
    return getViewportAwarePosition(
      config.position || 'top-right',
      384, // Default notification width
      100, // Estimated height
      viewportWidth,
      window.innerHeight,
      32 // 2rem edge offset
    );
  }, [config.position, viewportWidth]);

  // Calculate container styles
  const containerStyles = useMemo(() => {
    const baseStyles = getContainerPositionStyles(
      adjustedPosition,
      config.edgeOffset
    );

    // Adjust for header if position is at top
    if (adjustedPosition.includes('top') && headerHeight > 0) {
      const offset = getHeaderAwareOffset(
        adjustedPosition,
        headerHeight,
        config.edgeOffset?.top || '1rem'
      );
      baseStyles.top = offset;
    }

    // Adjust for notification panel on desktop (360px panel width)
    if (isPanelOpen && !isMobile && adjustedPosition.includes('right')) {
      const currentRight = baseStyles.right || config.edgeOffset?.right || '1rem';
      // Add 360px (panel width) to the right offset
      baseStyles.right = `calc(${currentRight} + 360px)`;
    }
    
    // Adjust for header dropdown on desktop (push toasts down)
    if (isDropdownOpen && dropdownHeight > 0 && !isMobile && adjustedPosition.includes('top')) {
      // Position 1rem below the bottom of the dropdown
      // Dropdown top is at header bottom (48px), so dropdown bottom = 48px + dropdownHeight
      const dropdownBottom = 48 + dropdownHeight;
      baseStyles.top = `calc(${dropdownBottom}px + 1rem)`;
    }

    // Apply mobile styles if needed
    if (isMobile) {
      Object.assign(baseStyles, getMobileStyles(true));
    }

    // Set z-index
    if (config.zIndex) {
      baseStyles.zIndex = config.zIndex;
    }

    // Set custom notification width as CSS variable
    const width = isMobile ? getResponsiveWidth(viewportWidth) : '24rem';
    // Use a type assertion to add CSS variable
    (baseStyles as any)['--notification-width'] = width;

    return baseStyles;
  }, [adjustedPosition, config.edgeOffset, config.zIndex, headerHeight, isMobile, viewportWidth, isPanelOpen, isDropdownOpen, dropdownHeight]);

  // Get visible notifications
  const visibleNotifications = useMemo(() => {
    return notifications
      .filter(n => n.animationState !== 'exited')
      .slice(0, config.maxVisible || 5);
  }, [notifications, config.maxVisible]);

  // Handle notification dismissal
  const handleDismiss = useCallback((id: string) => {
    onDismiss(id);
  }, [onDismiss]);

  // Handle animation completion
  const handleAnimationComplete = useCallback((id: string, state: AnimationState) => {
    onAnimationComplete(id, state);
  }, [onAnimationComplete]);

  // Create ARIA live region for announcements
  const ariaLiveRegion = useMemo(() => {
    const liveNotifications = notifications.filter(
      n => n.animationState === 'entering' && n.role !== 'alertdialog'
    );

    if (liveNotifications.length === 0) return null;

    return (
      <div
        className="notification-container__aria-live"
        role="region"
        aria-live="polite"
        aria-relevant="additions"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      >
        {liveNotifications.map(notification => (
          <div key={notification.id}>
            {notification.kind}: {notification.title}
            {notification.subtitle && ` - ${notification.subtitle}`}
          </div>
        ))}
      </div>
    );
  }, [notifications]);

  return (
    <>
      {/* ARIA Live Region for Screen Readers */}
      {ariaLiveRegion}

      {/* Notification Container */}
      <div 
        className="notification-container"
        data-position={adjustedPosition}
        style={{
          ...containerStyles,
          transition: 'right 0.25s ease-in-out',
        }}
        aria-label="Notifications"
      >
        {visibleNotifications.map((notification, index) => (
          <NotificationItem
            key={notification.id}
            {...notification}
            index={index}
            position={adjustedPosition}
            onClose={(event) => {
              if (notification.onClose) {
                const result = notification.onClose(event);
                if (result === false) return;
              }
              handleDismiss(notification.id);
            }}
            onCloseButtonClick={(event) => {
              if (notification.onCloseButtonClick) {
                notification.onCloseButtonClick(event);
              }
              handleDismiss(notification.id);
            }}
            onAnimationComplete={(state) => {
              handleAnimationComplete(notification.id, state);
            }}
          />
        ))}
      </div>
    </>
  );
};
